import { Controller, useFormContext } from 'react-hook-form';
import classNames from 'classnames';
import { FormControl } from './FormControl';
import { Loader } from '../ImageWithLoader';

export interface SwitchProps {
  checked: boolean;
  onChange: (value: boolean) => any;
  checkedColorClassName?: string;
  uncheckedColorClassName?: string;
  onBlur?: () => void;
  children?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  id?: string;
  horizontal?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

export interface FormSwitchProps {
  name: string;
  className?: string;
  label?: string;
  checkedColorClassName?: string;
  switchClassName?: string;
}

export function Switch({
  type = 'button',
  checkedColorClassName = 'bg-blue-600',
  uncheckedColorClassName = 'bg-gray-200',
  ...props
}: SwitchProps) {
  return (
    <button
      type={type}
      role="checkbox"
      aria-checked="false"
      onClick={() => props.onChange(!props.checked)}
      onBlur={props.onBlur}
      className={classNames(
        props.checked ? checkedColorClassName : uncheckedColorClassName,
        `relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
        ${props.horizontal ? 'mt-0' : 'mt-2'}`,
        props.className,
      )}
      disabled={props.disabled}
      id={props.id}
    >
      <span
        aria-hidden="true"
        className={classNames(
          props.checked ? 'translate-x-5' : 'translate-x-0',
          'pointer-events-none inline-block h-5 w-5 rounded-full bg-white  shadow transform ring-0 transition ease-in-out duration-200 relative',
        )}
      >
        {props.children}
      </span>
    </button>
  );
}

export function SwitchWithLoader({
  ...props
}: SwitchProps & { isLoading: boolean }) {
  return (
    <Switch
      {...props}
      checkedColorClassName={
        props.isLoading ? 'bg-blue-light' : props.checkedColorClassName
      }
      uncheckedColorClassName={
        props.isLoading ? 'bg-gray-300' : props.uncheckedColorClassName
      }
      disabled={props.isLoading}
    >
      {props.isLoading && (
        <Loader
          size="small"
          isLoading={props.isLoading}
          className="rounded-full"
        />
      )}
    </Switch>
  );
}

export function FormSwitch(props: FormSwitchProps) {
  const { control } = useFormContext();
  return (
    <FormControl
      label={props.label}
      name={props.name}
      className={props.className}
    >
      <Controller
        control={control}
        defaultValue={false}
        name={props.name}
        render={({ field }) => (
          <Switch
            onBlur={field.onBlur}
            checkedColorClassName={props.checkedColorClassName}
            checked={field.value}
            onChange={field.onChange}
            className={props.switchClassName}
          />
        )}
      />
    </FormControl>
  );
}
